'use client';

import Modal from '@/components/Modal';
import { IconArrowRightThin } from '@/icons/IconArrowRightThin';
import { IconGift } from '@/icons/IconGift';
import { IconLock } from '@/icons/IconLock';
import { IconPercentCircle } from '@/icons/IconPercentCircle';
import { IconShieldCheck } from '@/icons/IconShieldCheck';
import { IconShoppingBag } from '@/icons/IconShoppingBag';
import Image from 'next/image';

const STEPS = [
  {
    key: 'add-eligible',
    icon: IconShoppingBag,
    label: (
      <>
        Add more eligible
        <br />
        products
      </>
    ),
  },
  { key: 'unlock-discounts', icon: IconPercentCircle, label: 'Unlock bigger discounts' },
  { key: 'save-more', icon: IconGift, label: 'Save more on your order' },
];

const BundleOfferPrompt = ({
  open,
  onClose,
  onSkip,
  onExplore,
  skipLabel = 'Add to Cart',
  isExploring = false,
}: {
  open: boolean;
  onClose: () => void;
  onSkip: () => void;
  onExplore: () => void;
  skipLabel?: string;
  isExploring?: boolean;
}) => {
  return (
    <Modal
      open={open}
      closeModal={onClose}
      className="bundle-offer-dialog w-full md:w-[31.25rem]"
      bodyClassName="!p-6 !pt-14 md:!px-12 md:!pb-8 md:!pt-16"
      dialogClasses="!pb-0 md:w-[31.25rem] bg-[url('/images/bundle-offer-bg.svg')] bg-cover bg-center bg-no-repeat"
      roundedCloseButton
    >
      {/* Widths/spacing/type below are pulled directly from the Figma design (node 9747-415952):
          500px popup, 48px side padding, 16px gap between sections. */}
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex flex-col items-center gap-5">
          <div className="flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full bg-red-50">
            <IconGift className="h-8 w-8 text-red-600" />
          </div>

          <div className="flex flex-col items-center gap-3">
            <div className="text-[2rem] font-extrabold leading-[1.2] text-neutral-900">
              Build your bundle
              <br />
              <span className="text-red-600">&amp; save more!</span>
            </div>
            <div className="text-sm font-medium text-gray-500">Add more eligible products to unlock exciting discounts.</div>
          </div>
        </div>

        <div className="flex w-full items-start justify-center">
          {STEPS.map((step, index) => (
            <div key={step.key} className="flex items-start">
              <div className="flex w-[6.875rem] flex-col items-center gap-2.5">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-red-50">
                  <step.icon className="h-6 w-6 text-red-600" />
                </div>
                <div className="text-[11px] font-bold leading-snug text-neutral-900">{step.label}</div>
              </div>
              {index < STEPS.length - 1 && <IconArrowRightThin className="mt-6 w-4 shrink-0 text-gray-500" aria-hidden="true" />}
            </div>
          ))}
        </div>

        <div className="flex w-full flex-col gap-3">
          <button
            data-testid="explore-bundle-offers-button"
            type="button"
            className="flex h-[50px] w-full cursor-pointer items-center justify-center gap-2 rounded-[10px] bg-black !text-[15px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onExplore}
            disabled={isExploring}
          >
            {isExploring ? 'Loading...' : 'Explore Bundle Offers'}
            {!isExploring && <IconArrowRightThin className="w-4" aria-hidden="true" />}
          </button>
          <button
            data-testid="maybe-later-button"
            type="button"
            className="flex h-[50px] w-full cursor-pointer items-center justify-center rounded-[10px] border-2 border-black !text-[15px] font-bold transition duration-150 ease-linear hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onSkip}
            disabled={isExploring}
          >
            {skipLabel}
          </button>
        </div>

        <div className="flex w-full items-center justify-between text-[11px] font-semibold text-gray-500">
          <span className="flex items-center gap-1.5">
            <IconShieldCheck className="h-4 w-4 text-red-600" />
            Easy returns, always
          </span>
          <span className="flex items-center gap-1.5">
            <IconLock className="h-4 w-4 text-red-600" />
            Secure checkout
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default BundleOfferPrompt;
